// src/services/cmdbService.ts
// Service para CMDB QOps (Q-870 D2) — equipos, sistemas, accesos, bootstrap tokens
import { api } from './api';
import type { PaginatedResponse } from '@/types';
import type {
  Acceso,
  AccesoCreate,
  BootstrapToken,
  BootstrapTokenCreated,
  Equipo,
  EquipoCreate,
  SistemaInstalado,
  SistemaProyecto,
} from '@/types/cmdb';

interface EquipoFilters {
  cliente_id?: string;
  activo?: boolean;
  page?: number;
  page_size?: number;
}

interface SistemaFilters {
  cliente_id?: string;
  equipo_id?: string;
  sistema?: SistemaProyecto;
  page?: number;
  page_size?: number;
}

interface AccesoFilters {
  cliente_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}

export type DeployHealthStatus = 'match' | 'gap' | 'drift' | 'unknown' | 'rogue';

export interface DeployHealthRow {
  id: string;
  cliente_id: string;
  sistema_instalado_id: string;
  sistema: string;
  bd: string | null;
  artifact_id: string;
  version_expected: string;
  version_applied: string | null;
  checksum_expected: string;
  checksum_observed: string | null;
  status: DeployHealthStatus;
  last_audit_at: string | null;
  last_audit_run_id: string | null;
  source?: 'pull' | 'push';  // Q-916: origen del row
}

// Q-918: ManifestArtifact + ManifestSnapshot
export interface ManifestArtifact {
  artifact_id: string;
  type: string | null;
  phase: string | null;
  path: string | null;
  version: string | null;
  checksum_sha256: string | null;
  since_version: string | null;
  scope: string | null;
  required_for_empresa: string | null;
  notes: string | null;
  bd: string | null;
}

export interface ManifestSnapshotResponse {
  sistema: string;
  version: string | null;
  target_stack: string | null;
  source_ref: string;
  manifest_hash: string;
  fetched_at: string | null;
  phases: string[];
  artifacts: ManifestArtifact[];
}

// Q-927: Script content viewer + Q-927 refresh manifest
export interface ScriptContentResponse {
  sistema: string;
  artifact_id: string;
  path: string;
  content_text: string;
  content_size_bytes: number;
  sha256_actual: string;
  fetch_status: 'ok' | 'not_found' | 'fetch_error';
  fetched_at: string | null;
  snapshot_age_days: number | null;
  snapshot_id: string;
  manifest_hash: string;
}

export interface RefreshManifestResponse {
  status: string;
  sistema: string;
  artifacts_count: number;
  rate_limit_seconds_remaining: number;
}

// Q-926: Deploy plan
export type ArtifactEstadoActual = DeployHealthStatus | 'no_aplicado';

export interface DeployPlanArtifact {
  artifact_id: string;
  type: string;
  phase: string;
  path: string | null;
  scope: string;
  required_for_empresa: string;
  checksum_expected: string | null;
  version: string | null;
  since_version: string | null;
  bd: string | null;
  notes: string | null;
  aplica: boolean;
  skip_reason: string | null;
  estado_actual: ArtifactEstadoActual;
  checksum_observed: string | null;
  version_applied: string | null;
  last_audit_at: string | null;
  script_url: string;
}

export interface DeployPlanFase {
  phase: string;
  artifacts: DeployPlanArtifact[];
}

export interface DeployPlanResponse {
  sistema: string;
  version: string | null;
  snapshot_id: string;
  snapshot_age_days: number | null;
  manifest_hash: string;
  target: {
    cliente_id: string;
    cliente_nombre: string;
    bd: string | null;
    empresa_id: string | null;
    empresa_nombre: string | null;
  };
  features_verificadas: boolean;
  totales: {
    manifest: number;
    aplicables: number;
    aplicados: number;
    pendientes: number;
    skip_por_licencia: number;
    skip_por_bd: number;
    skip_por_scope: number;
  };
  fases: DeployPlanFase[];
}

export interface DeployPlanParams {
  sistema: string;
  cliente_id: string;
  bd?: string;
  empresa_id?: string;
}

// =============================================================================
// Q-928 + Q-929 Sprint 7 — Apply tokens + Apply runs
// =============================================================================

export interface ApplyTokenCreatePayload {
  artifact_id: string;
  cliente_id: string;
  empresa_id?: string;
  bd?: string;
  scope?: Record<string, unknown>;
  dry_run: boolean;
  ttl_seconds?: number;
}

export interface ApplyTokenCreated {
  token_id: string;
  signed_token: string;
  artifact_id: string;
  cliente_id: string;
  bd: string | null;
  dry_run: boolean;
  expires_at: string;
  created_at: string;
}

export interface ApplyTokenSummary {
  id: string;
  nonce: string;
  artifact_id: string;
  cliente_id: string;
  empresa_id: string | null;
  bd: string | null;
  scope: Record<string, unknown>;
  dry_run: boolean;
  created_by: string;
  created_at: string;
  ttl_expires_at: string;
  used_at: string | null;
  used_by_run_id: string | null;
  revoked_at: string | null;
  revoked_by: string | null;
}

export type ApplyRunStatus =
  | 'dry_run_ok'
  | 'applied'
  | 'failed'
  | 'token_invalid'
  | 'checksum_mismatch'
  | 'bitacora_missing'
  | 'token_mismatch'
  | 'already_applied';

export interface ApplyRun {
  id: string;
  apply_token_id: string;
  agente_id: string | null;
  cliente_id: string;
  empresa_id: string | null;
  bd: string | null;
  artifact_id: string;
  status: ApplyRunStatus;
  dry_run: boolean;
  checksum_expected: string;
  duration_ms: number | null;
  error_log: string | null;
  bitacora_id: string | null;
  snapshot_previo_size_bytes: number | null;
  applied_at: string | null;
  created_at: string;
}

export interface ApplyHistoryFilters {
  cliente_id?: string;
  artifact_id?: string;
  status_filter?: ApplyRunStatus;
  page?: number;
  page_size?: number;
}

// Q-939 Sprint 8: forward apply backend->agente
export interface ApplyViaAgentPayload {
  signed_token: string;
  artifact_id: string;
  artifact_kind: string;
  content_text: string;
  checksum_expected: string;
  project: string;
  cliente_id: string;
  bd?: string;
  dry_run: boolean;
  admin_user: string;
}

export interface ApplyViaAgentResponse {
  apply_run_id: string;
  forward_status: 'success' | 'agent_rejected' | 'agent_unreachable';
  agent_status_code: number;
  agent_response: Record<string, unknown> & { status?: string; message?: string };
}

export interface AuditRunSummary {
  id: string;
  started_at: string | null;
  finished_at: string | null;
  status: 'running' | 'completed' | 'failed';
  trigger_source: string | null;
  triggered_by_user_id?: string | null;
  agentes_total?: number | null;
  agentes_ok?: number | null;
  agentes_unreachable?: number | null;
  sistemas_checked?: number | null;
  matches?: number | null;
  gaps_found?: number | null;
  drifts_found?: number | null;
  rogues_found?: number | null;
  errors?: number | null;
  error_log?: Record<string, string> | null;
}

interface DeployHealthFilters {
  cliente_id?: string;
  sistema?: string;
  status?: DeployHealthStatus;
  limit?: number;
}

function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

export const cmdbService = {
  // Equipos
  listEquipos: (f?: EquipoFilters) =>
    api.get<PaginatedResponse<Equipo>>(`/cmdb/equipos${qs(f ?? {})}`),
  getEquipo: (id: string) => api.get<Equipo>(`/cmdb/equipos/${id}`),
  createEquipo: (data: EquipoCreate) => api.post<Equipo>('/cmdb/equipos', data),
  updateEquipo: (id: string, data: Partial<EquipoCreate>) =>
    api.patch<Equipo>(`/cmdb/equipos/${id}`, data),

  // Sistemas
  listSistemas: (f?: SistemaFilters) =>
    api.get<PaginatedResponse<SistemaInstalado>>(`/cmdb/sistemas${qs(f ?? {})}`),
  getSistema: (id: string) => api.get<SistemaInstalado>(`/cmdb/sistemas/${id}`),

  // Accesos
  listAccesos: (f?: AccesoFilters) =>
    api.get<PaginatedResponse<Acceso>>(`/cmdb/accesos${qs(f ?? {})}`),
  createAcceso: (data: AccesoCreate) => api.post<Acceso>('/cmdb/accesos', data),
  updateAcceso: (id: string, data: Partial<AccesoCreate>) =>
    api.patch<Acceso>(`/cmdb/accesos/${id}`, data),
  deleteAcceso: (id: string) => api.delete<void>(`/cmdb/accesos/${id}`),

  // Bootstrap tokens (admin)
  listBootstrapTokens: (cliente_id?: string) =>
    api.get<PaginatedResponse<BootstrapToken>>(`/cmdb/bootstrap-tokens${qs({ cliente_id })}`),
  createBootstrapToken: (cliente_id: string, ttl_hours = 72) =>
    api.post<BootstrapTokenCreated>('/cmdb/bootstrap-tokens', { cliente_id, ttl_hours }),
  revokeBootstrapToken: (id: string) => api.delete<void>(`/cmdb/bootstrap-tokens/${id}`),

  // Deploy health + audit runs (Q-873-D5 + Q-912)
  listDeployHealth: (f?: DeployHealthFilters) =>
    api.get<DeployHealthRow[]>(`/cmdb/deploy-health${qs(f ?? {})}`),
  triggerAuditRun: () =>
    api.post<{ run_id: string; status: string }>('/cmdb/audit-run/trigger', {}),
  getAuditRun: (run_id: string) =>
    api.get<AuditRunSummary>(`/cmdb/audit-run/${run_id}`),
  listAuditRuns: (limit = 20) =>
    api.get<AuditRunSummary[]>(`/cmdb/audit-run${qs({ limit })}`),

  // Q-918: Manifests viewer
  getManifest: (sistema: string) =>
    api.get<ManifestSnapshotResponse>(`/cmdb/manifests/${sistema}`),

  // Q-927: Script content cache viewer + refresh manifest
  getScriptContent: (sistema: string, artifactId: string) =>
    api.get<ScriptContentResponse>(
      `/cmdb/scripts/${encodeURIComponent(sistema)}/${encodeURIComponent(artifactId)}/content`,
    ),
  refreshManifest: (sistema: string) =>
    api.post<RefreshManifestResponse>(`/cmdb/manifests/${sistema}/refresh`, {}),

  // Q-926: Deploy plan PRE-deploy
  getDeployPlan: (params: DeployPlanParams) =>
    api.get<DeployPlanResponse>(`/cmdb/deploy-plan${qs(params)}`),

  // Q-928: Apply tokens (admin emite/revoca/lista)
  createApplyToken: (payload: ApplyTokenCreatePayload) =>
    api.post<ApplyTokenCreated>('/cmdb/apply-tokens', payload),
  listApplyTokens: (filters?: { cliente_id?: string; artifact_id?: string; status_filter?: string }) =>
    api.get<PaginatedResponse<ApplyTokenSummary>>(`/cmdb/apply-tokens${qs(filters ?? {})}`),
  revokeApplyToken: (token_id: string) =>
    api.post<ApplyTokenSummary>(`/cmdb/apply-tokens/${token_id}/revoke`, {}),

  // Q-929: Apply history (ledger audit del agente)
  listApplyHistory: (filters?: ApplyHistoryFilters) =>
    api.get<PaginatedResponse<ApplyRun>>(`/cmdb/apply-history${qs(filters ?? {})}`),

  // Q-939 Sprint 8: forward backend->agente (sin curl manual)
  applyViaAgent: (payload: ApplyViaAgentPayload) =>
    api.post<ApplyViaAgentResponse>('/cmdb/apply-via-agent', payload),
};
